/**
 * urlSanitizer - URL scheme validation utility
 *
 * Security: Prevents stored XSS via malicious project URLs.
 * An attacker could store `javascript:alert(1)` or a `data:` URI as a
 * project URL; this utility ensures only safe http/https schemes are
 * rendered as clickable anchors.
 *
 * Feature: Epic 1.2 – Security Hardening / Feature 1.2.2
 */

/** Schemes that are safe to render as an <a href>. */
const ALLOWED_SCHEMES = ['https:', 'http:'] as const;

/**
 * Validates whether a URL has an allowed (http / https) scheme.
 *
 * @param url - The raw URL string from the API.
 * @returns `true` when the URL is safe to render as a clickable link.
 *
 * @example
 * isSafeUrl('https://example.com')      // true
 * isSafeUrl('http://example.com')       // true
 * isSafeUrl('javascript:alert(1)')      // false
 * isSafeUrl('data:text/html,<h1>x</h1>') // false
 * isSafeUrl('vbscript:MsgBox(1)')       // false
 * isSafeUrl('/relative/path')           // false
 * isSafeUrl('')                         // false
 */
export function isSafeUrl(url: string | undefined | null): boolean {
  if (!url || typeof url !== 'string') return false;

  try {
    const parsed = new URL(url);
    return (ALLOWED_SCHEMES as readonly string[]).includes(parsed.protocol);
  } catch {
    // URL constructor throws on relative URLs and malformed strings
    return false;
  }
}

/**
 * Sanitizes a URL for use as an `<a href>` attribute value.
 *
 * Returns the original URL string when its scheme is http or https.
 * Returns `null` for all other cases so the caller can render plain text
 * instead of a clickable link.
 *
 * @param url - The raw URL string from the API.
 * @returns The safe URL string, or `null` if the URL is unsafe / invalid.
 *
 * @example
 * sanitizeUrl('https://example.com')   // 'https://example.com'
 * sanitizeUrl('javascript:alert(1)')   // null
 * sanitizeUrl('/relative')             // null
 * sanitizeUrl(undefined)               // null
 */
export function sanitizeUrl(url: string | undefined | null): string | null {
  if (!isSafeUrl(url)) return null;
  // isSafeUrl already guarantees url is a non-empty string here
  return url as string;
}
